import { api } from "@/lib/api";

/**
 * 424: upozornění na displej (Web Push) — převzato z ProfitPilotu. Telefon s Androidem a počítač
 * hned, iPhone jen s Proyekty přidaným na plochu (Sdílet → Přidat na plochu, iOS 16.4+).
 * Server posílá na zařízení přihlášeného člověka; po odhlášení se odběr zruší.
 * Zvuk, vibrace a Nerušit řídí telefon sám.
 */
export const canPush = () => typeof window !== "undefined"
  && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
export const isIos = () => typeof navigator !== "undefined" && (/iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
export const onHomeScreen = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const isPhone = () => typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

let registration = null;
export function swReady() {
  if (!("serviceWorker" in navigator)) return Promise.resolve(null);
  if (!registration) registration = navigator.serviceWorker.register("/service-worker.js").then(() => navigator.serviceWorker.ready).catch(() => null);
  return registration;
}

function keyBytes(b64) {
  const s = atob((b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
}

/** "on" | "off" | "denied" (telefon zakázal) | "ios-home" (nejdřív na plochu) | "unsupported" */
export async function pushState() {
  if (!canPush()) return isIos() && !onHomeScreen() ? "ios-home" : "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await swReady();
  const sub = reg && (await reg.pushManager.getSubscription());
  return sub && Notification.permission === "granted" ? "on" : "off";
}

/** Volat přímo z klepnutí — prohlížeč se zeptá na povolení jen po akci člověka. */
export async function enablePush() {
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return perm === "denied" ? "denied" : "off";
  const reg = await swReady();
  if (!reg) return "unsupported";
  const { data } = await api.get("/push/key");
  const sub = (await reg.pushManager.getSubscription())
    || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(data.key) }));
  await api.post("/push/subscribe", sub.toJSON());
  return "on";
}

/** Vypnout na tomto zařízení. */
export async function disablePush() {
  const reg = await swReady();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  return "off";
}

/** Po přihlášení: zařízení s povolením dostává upozornění právě přihlášeného člověka. */
export async function renewPush() {
  if (!canPush() || Notification.permission !== "granted") return;
  const reg = await swReady();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) await api.post("/push/subscribe", sub.toJSON()).catch(() => {});
}

/** Odhlášení: na tohle zařízení už upozornění tohoto člověka nechodí. */
export async function forgetPush() {
  if (!canPush()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    if (sub) await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch { /* odhlášení nesmí spadnout */ }
}
